import { memo, type PointerEvent, useCallback } from 'react';

import { type WorkspaceAgentPanelResizeEdges } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentPanelResizeEdges';

const RESIZE_TOP: WorkspaceAgentPanelResizeEdges = { bottom: false, left: false, right: false, top: true };
const RESIZE_RIGHT: WorkspaceAgentPanelResizeEdges = { bottom: false, left: false, right: true, top: false };
const RESIZE_BOTTOM: WorkspaceAgentPanelResizeEdges = { bottom: true, left: false, right: false, top: false };
const RESIZE_LEFT: WorkspaceAgentPanelResizeEdges = { bottom: false, left: true, right: false, top: false };
const RESIZE_TOP_LEFT: WorkspaceAgentPanelResizeEdges = { bottom: false, left: true, right: false, top: true };
const RESIZE_TOP_RIGHT: WorkspaceAgentPanelResizeEdges = { bottom: false, left: false, right: true, top: true };
const RESIZE_BOTTOM_LEFT: WorkspaceAgentPanelResizeEdges = { bottom: true, left: true, right: false, top: false };
const RESIZE_BOTTOM_RIGHT: WorkspaceAgentPanelResizeEdges = { bottom: true, left: false, right: true, top: false };

export type WorkspaceAgentPanelFloatResizeHandlesProps = {
  onResizePointerDown: (edges: WorkspaceAgentPanelResizeEdges) => (event: PointerEvent<HTMLElement>) => void;
};

export type WorkspaceAgentPanelFloatResizeHandleProps = {
  className: string;
  edges: WorkspaceAgentPanelResizeEdges;
  onResizePointerDown: WorkspaceAgentPanelFloatResizeHandlesProps['onResizePointerDown'];
  testId: string;
};

const WorkspaceAgentPanelFloatResizeHandle = memo(
  ({ className, edges, onResizePointerDown, testId }: WorkspaceAgentPanelFloatResizeHandleProps) => {
    const handlePointerDown = useCallback(
      (event: PointerEvent<HTMLDivElement>) => {
        onResizePointerDown(edges)(event);
      },
      [edges, onResizePointerDown],
    );

    return (
      <div
        aria-hidden
        className={className}
        data-testid={testId}
        data-workspace-agent-panel-resize=""
        onPointerDown={handlePointerDown}
      />
    );
  },
);

WorkspaceAgentPanelFloatResizeHandle.displayName = 'WorkspaceAgentPanelFloatResizeHandle';

export const WorkspaceAgentPanelFloatResizeHandles = memo(
  ({ onResizePointerDown }: WorkspaceAgentPanelFloatResizeHandlesProps) => (
    <>
      <WorkspaceAgentPanelFloatResizeHandle
        className="absolute top-0 right-3 left-3 z-20 h-1.5 cursor-ns-resize touch-none"
        edges={RESIZE_TOP}
        onResizePointerDown={onResizePointerDown}
        testId="WORKSPACE_AGENT_PANEL_RESIZE_TOP"
      />
      <WorkspaceAgentPanelFloatResizeHandle
        className="absolute top-3 right-0 bottom-3 z-20 w-1.5 cursor-ew-resize touch-none"
        edges={RESIZE_RIGHT}
        onResizePointerDown={onResizePointerDown}
        testId="WORKSPACE_AGENT_PANEL_RESIZE_RIGHT"
      />
      <WorkspaceAgentPanelFloatResizeHandle
        className="absolute right-3 bottom-0 left-3 z-20 h-1.5 cursor-ns-resize touch-none"
        edges={RESIZE_BOTTOM}
        onResizePointerDown={onResizePointerDown}
        testId="WORKSPACE_AGENT_PANEL_RESIZE_BOTTOM"
      />
      <WorkspaceAgentPanelFloatResizeHandle
        className="absolute top-3 bottom-3 left-0 z-20 w-1.5 cursor-ew-resize touch-none"
        edges={RESIZE_LEFT}
        onResizePointerDown={onResizePointerDown}
        testId="WORKSPACE_AGENT_PANEL_RESIZE_LEFT"
      />
      <WorkspaceAgentPanelFloatResizeHandle
        className="absolute top-0 left-0 z-20 size-3 cursor-nwse-resize touch-none"
        edges={RESIZE_TOP_LEFT}
        onResizePointerDown={onResizePointerDown}
        testId="WORKSPACE_AGENT_PANEL_RESIZE_TOP_LEFT"
      />
      <WorkspaceAgentPanelFloatResizeHandle
        className="absolute top-0 right-0 z-20 size-3 cursor-nesw-resize touch-none"
        edges={RESIZE_TOP_RIGHT}
        onResizePointerDown={onResizePointerDown}
        testId="WORKSPACE_AGENT_PANEL_RESIZE_TOP_RIGHT"
      />
      <WorkspaceAgentPanelFloatResizeHandle
        className="absolute bottom-0 left-0 z-20 size-3 cursor-nesw-resize touch-none"
        edges={RESIZE_BOTTOM_LEFT}
        onResizePointerDown={onResizePointerDown}
        testId="WORKSPACE_AGENT_PANEL_RESIZE_BOTTOM_LEFT"
      />
      <WorkspaceAgentPanelFloatResizeHandle
        className="absolute right-0 bottom-0 z-20 size-3 cursor-nwse-resize touch-none"
        edges={RESIZE_BOTTOM_RIGHT}
        onResizePointerDown={onResizePointerDown}
        testId="WORKSPACE_AGENT_PANEL_RESIZE_BOTTOM_RIGHT"
      />
    </>
  ),
);

WorkspaceAgentPanelFloatResizeHandles.displayName = 'WorkspaceAgentPanelFloatResizeHandles';
